import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import '../App.css';
import { FaChartBar, FaMoneyBillWave } from 'react-icons/fa';

const API_BASE_URL =
  process.env.REACT_APP_DERIV_API_BASE_URL || 'https://api.derivws.com';
const AUTO_REFRESH_MS = 30000;
const DEFAULT_DERIV_APP_ID = '33nQBGQDYcipshLSScVV1';
const DEFAULT_ACCESS_TOKEN =
  'pat_c37373060de0bd9764e77b7cfe56b1c9cdea06ee7a65902fde05dbdd88782f16';
const USD_TO_KES = 128;
const EXCLUDED_APP_IDS = new Set(['33l3KoJ3qrX7Qm2jaiJXn']);
const APP_LABELS = {
  '336YRuUI7T1Xm1nRcTALu': '360 Site V2',
  '336Xi8ZTwlg3CVbATWZga': 'Kim Traders V2',
};

const formatUsd = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatNumber = (value) =>
  new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(value || 0);

const formatKes = (usdValue) =>
  `KES ${formatNumber((usdValue || 0) * USD_TO_KES)}`;

const getTodayUtc = () => new Date().toISOString().slice(0, 10);

const getYesterdayUtc = () => {
  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);

  return yesterday.toISOString().slice(0, 10);
};

const getLastEightUtcDates = () =>
  Array.from({ length: 8 }, (_, index) => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - (7 - index));

    return date.toISOString().slice(0, 10);
  });

const getUtcDayLabel = (dateValue) =>
  new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  }).format(new Date(`${dateValue}T00:00:00Z`));

const getCurrentMonthUtc = () => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');

  return `${year}-${month}`;
};

const getMonthDateRange = (monthValue) => {
  const [yearText, monthText] = monthValue.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const today = getTodayUtc();
  const currentMonth = getCurrentMonthUtc();
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();

  return {
    dateFrom: `${monthValue}-01`,
    dateTo:
      monthValue === currentMonth
        ? today
        : `${yearText}-${monthText}-${String(lastDay).padStart(2, '0')}`,
  };
};

const getErrorMessage = (error) => {
  const apiMessage = error.response?.data?.errors?.[0]?.message;
  const status = error.response?.status;

  if (status === 401) {
    return (
      apiMessage ||
      'Unauthorized. Check that your token is valid, has application_read scope, and that Deriv App ID is set.'
    );
  }

  if (apiMessage) {
    return apiMessage;
  }

  return error.message || 'Unable to fetch markup statistics.';
};

const getAppTitle = (appId) => APP_LABELS[appId] || appId;

const sumBreakdownValue = (breakdown, key) =>
  breakdown.reduce((total, item) => total + (Number(item[key]) || 0), 0);

const excludeAppStatistics = (payload) => {
  const breakdown = payload?.data?.breakdown;

  if (!Array.isArray(breakdown)) {
    return payload;
  }

  const filteredBreakdown = breakdown.filter(
    (item) => !EXCLUDED_APP_IDS.has(item.app_id)
  );

  return {
    ...payload,
    data: {
      ...payload.data,
      breakdown: filteredBreakdown,
      total_app_markup_usd: sumBreakdownValue(
        filteredBreakdown,
        'app_markup_usd'
      ),
      total_client_count: sumBreakdownValue(filteredBreakdown, 'client_count'),
      total_contract_count: sumBreakdownValue(
        filteredBreakdown,
        'contract_count'
      ),
      total_payout_usd: sumBreakdownValue(filteredBreakdown, 'payout_usd'),
      total_volume_usd: sumBreakdownValue(filteredBreakdown, 'volume_usd'),
    },
  };
};

const fetchMarkupStatistics = async ({
  accessToken,
  derivAppId,
  dateFrom,
  dateTo,
}) => {
  const response = await axios.get(
    `${API_BASE_URL}/applications/v1/markup-statistics`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Deriv-App-ID': derivAppId,
      },
      params: {
        date_from: dateFrom,
        date_to: dateTo,
      },
    }
  );

  return excludeAppStatistics(response.data);
};

function NewApi({
  initialAccessToken =
    process.env.REACT_APP_DERIV_ACCESS_TOKEN || DEFAULT_ACCESS_TOKEN,
  initialDerivAppId =
    process.env.REACT_APP_DERIV_APP_ID || DEFAULT_DERIV_APP_ID,
  onShowHome,
}) {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthUtc);
  const [rangePayload, setRangePayload] = useState(null);
  const [todayPayload, setTodayPayload] = useState(null);
  const [yesterdayPayload, setYesterdayPayload] = useState(null);
  const [weekPayloads, setWeekPayloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const accessToken = initialAccessToken;
  const derivAppId = initialDerivAppId;

  const loadStatistics = useCallback(async ({ silent = false } = {}) => {
    if (!accessToken || !derivAppId || !selectedMonth) {
      setRangePayload(null);
      setTodayPayload(null);
      setYesterdayPayload(null);
      setWeekPayloads([]);
      setError('');
      setLoading(false);
      return;
    }

    setError('');

    if (silent) {
    } else {
      setLoading(true);
    }

    const { dateFrom, dateTo } = getMonthDateRange(selectedMonth);
    const today = getTodayUtc();
    const yesterday = getYesterdayUtc();
    const weekDates = getLastEightUtcDates();

    try {
      const [rangeResponse, ...weekResponses] = await Promise.all([
        fetchMarkupStatistics({
          accessToken,
          derivAppId,
          dateFrom,
          dateTo,
        }),
        ...weekDates.map((date) =>
          fetchMarkupStatistics({
            accessToken,
            derivAppId,
            dateFrom: date,
            dateTo: date,
          })
        ),
      ]);

      const dailyPayloads = weekDates.map((date, index) => ({
        date,
        payload: weekResponses[index],
      }));

      setRangePayload(rangeResponse);
      setTodayPayload(
        dailyPayloads.find((item) => item.date === today)?.payload || null
      );
      setYesterdayPayload(
        dailyPayloads.find((item) => item.date === yesterday)?.payload || null
      );
      setWeekPayloads(dailyPayloads);
      setLastUpdated(new Date().toLocaleString());
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [accessToken, derivAppId, selectedMonth]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  useEffect(() => {
    if (!accessToken || !derivAppId || !selectedMonth) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadStatistics({ silent: true });
    }, AUTO_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [accessToken, derivAppId, selectedMonth, loadStatistics]);

  const rangeStats = rangePayload?.data;
  const todayStats = todayPayload?.data;
  const yesterdayStats = yesterdayPayload?.data;
  const breakdown = rangeStats?.breakdown || [];

  return (
    <div className="app-wrapper">
      <div className="app-container">
        {onShowHome && (
          <div className="actions-row top-nav-actions">
            <button
              type="button"
              className="refresh-button"
              onClick={onShowHome}
            >
              Home
            </button>
          </div>
        )}

        <h1>
          <FaChartBar /> Mark-Up Statistics
        </h1>

        <div className="filters-panel">
          <div className="input-grid">
            <label className="field-group">
              <span>Selected Month:</span>
              <input
                type="month"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
              />
            </label>
          </div>

    

          {lastUpdated && (
            <p className="status-note">Last updated: {lastUpdated}</p>
          )}
          
        </div>

        {!accessToken || !derivAppId ? (
          <div className="commission-card empty-state-card">
            Missing saved API credentials in this component.
          </div>
        ) : loading && !rangeStats ? (
          <div className="loader-page">
            <div className="loader"></div>
            <p>Loading statistics...</p>
          </div>
        ) : (
          <div className="card-container">
            <div className="commission-card total-summary">
              <p className="text-header">This month</p>
              <p className="total-mark-kes">
                {formatKes(rangeStats?.total_app_markup_usd)}
              </p>
              <p className="total-mark">{formatUsd(rangeStats?.total_app_markup_usd)}</p>
              <p className="total-trades">
                {formatNumber(rangeStats?.total_contract_count)} contracts
              </p>
              <p className="green-text">
                {formatNumber(rangeStats?.total_client_count)} active clients
              </p>
            </div>

            <div className="commission-card today-summary">
              <p className="text-header">Today's Commission</p>
              <p className="usd-data">{formatUsd(todayStats?.total_app_markup_usd)}</p>
              <p className="price-kes">
                {formatKes(todayStats?.total_app_markup_usd)}
              </p>
              <p className="green-text">
                {formatNumber(todayStats?.total_contract_count)} contracts
              </p>
              <p className="total-trades">
                {formatNumber(todayStats?.total_client_count)} active clients
              </p>
            </div>

            <div className="commission-card yesterday-summary">
              <p className="text-header">Yesterday's Commission</p>
              <p className="usd-data">
                {formatUsd(yesterdayStats?.total_app_markup_usd)}
              </p>
              <p className="price-kes">
                {formatKes(yesterdayStats?.total_app_markup_usd)}
              </p>
              <p className="green-text">
                {formatNumber(yesterdayStats?.total_contract_count)} contracts
              </p>
              <p className="total-trades">
                {formatNumber(yesterdayStats?.total_client_count)} active clients
              </p>
            </div>
{/* 
            <div className="commission-card">
              <p className="text-header">Range Volume</p>
              <p className="usd-data">{formatUsd(rangeStats?.total_volume_usd)}</p>
              <p className="price-kes">
                {formatKes(rangeStats?.total_volume_usd)}
              </p>
              <p className="green-text">
                Payout: {formatUsd(rangeStats?.total_payout_usd)}
              </p>
              <p className="total-trades">
                API timing: {formatNumber(rangePayload?.meta?.timing)} ms
              </p>
            </div>
            */}

            {breakdown.length === 0 ? (
              <div className="commission-card empty-state-card">
                No application statistics were returned for this date range.
              </div>
            ) : (
              breakdown.map((item) => (
                <div className="commission-card" key={item.app_id}>
                  <p className="text-header">
                    {getAppTitle(item.app_id)} <FaMoneyBillWave />
                  </p>
                  <p className="usd-data">{formatUsd(item.app_markup_usd)}</p>
                  <p className="price-kes">
                    {formatKes(item.app_markup_usd)}
                  </p>
                  <p className="green-text">
                    {formatNumber(item.contract_count)} contracts
                  </p>
                 
                  <p className="total-trades">
                    {formatNumber(item.client_count)} clients | Payout:{' '}
                    {formatUsd(item.payout_usd)}
                  </p>
                </div>
              ))
            )}
          </div>
        )}

        {error && <p className="error">{error}</p>}

        {weekPayloads.length > 0 && (
          <footer className="weekly-kes-footer" aria-label="Last eight days commission">
            <div className="weekly-kes-scroll">
              {weekPayloads.map(({ date, payload }) => (
                <div className="weekly-kes-day" key={date}>
                  <span className="weekly-kes-label">{getUtcDayLabel(date)}</span>
                  <span className="weekly-kes-amount">
                    {formatKes(payload?.data?.total_app_markup_usd)}
                  </span>
                </div>
              ))}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}

export default NewApi;
